import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Star, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { name: "", service: "", amount: "", rating: 5, text: "" };

export default function ReviewsPage() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const fetchItems = async () => {
    const res = await api.get("/public-reviews");
    setItems(res.data || []);
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ name: r.name, service: r.service || "", amount: r.amount || "", rating: r.rating || 5, text: r.text }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.text) return toast.error("Name and review text required");
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating) };
      if (editing) await api.put(`/reviews/${editing.id}`, payload);
      else await api.post("/reviews", payload);
      toast.success(editing ? "Review updated" : "Review added");
      setOpen(false);
      fetchItems();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteId) return;
    try {
      await api.delete(`/reviews/${deleteId}`);
      toast.success("Deleted");
      setDeleteId(null);
      fetchItems();
    } catch { toast.error("Failed"); }
  };

  return (
    <div data-testid="reviews-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>Reviews</h1>
          <p className="text-sm text-muted-foreground mt-1">{items.length} custom reviews • shown on landing page</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="reviews-add-btn">
          <Plus className="h-4 w-4 mr-1" /> Add review
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="mt-8 rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          No custom reviews yet. Add one to feature it on the homepage.
        </div>
      ) : (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {items.map((r) => (
            <div key={r.id} className="rounded-xl border border-border bg-card p-5" data-testid={`review-row-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: r.rating || 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-primary text-primary" />)}
                  </div>
                  <div className="mt-2 font-semibold">{r.name}</div>
                  <div className="text-xs text-muted-foreground">{r.service}{r.amount ? ` · ${r.amount}` : ""}</div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(r)} className="h-8 w-8 rounded-md border border-border hover:border-primary hover:text-primary flex items-center justify-center" aria-label="Edit" data-testid={`review-edit-${r.id}`}>
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => setDeleteId(r.id)} className="h-8 w-8 rounded-md border border-border hover:border-destructive hover:text-destructive flex items-center justify-center" aria-label="Delete" data-testid={`review-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
              <p className="mt-3 text-sm text-foreground/90 leading-relaxed line-clamp-4">{r.text}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-lg" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: "Outfit" }}>{editing ? "Edit review" : "New review"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Customer name *</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="bg-background mt-1" data-testid="review-name-input" />
              </div>
              <div>
                <Label>Rating (1-5)</Label>
                <Input type="number" min={1} max={5} value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="bg-background mt-1" data-testid="review-rating-input" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Service</Label>
                <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="bg-background mt-1" placeholder="e.g. Local Moving" />
              </div>
              <div>
                <Label>Amount / cost</Label>
                <Input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="bg-background mt-1" placeholder="e.g. $400" />
              </div>
            </div>
            <div>
              <Label>Review text *</Label>
              <Textarea rows={5} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required className="bg-background mt-1" data-testid="review-text-input" />
            </div>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground" data-testid="review-save-btn">
                {saving ? "..." : "Save"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(v) => !v && setDeleteId(null)}>
        <AlertDialogContent className="bg-card border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this review?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from the homepage carousel.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="rounded-full bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
